import React, { useState } from 'react';
import { Box, IconButton, Link } from '@mui/material';
import { AttachmentMetaDataModel, NewsItemModel } from '../../../../api-service/clients';
import { FormDialog } from '../../../../components/form-dialog';
import { TextField } from '../../../../components/text-field/text-field';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { Row } from '../../../../components/row/row';
import { Cell } from '../../../../components/cell/cell';
import { TrashIcon } from '../../../../components/icons/trash-icon/trash-icon';
import { toDate, toStartOfDay, today } from '../../../../utilities/date-helper';
import { MultiFilePicker } from '../../../../components/multi-file-picker/multi-file-picker';
import { forumAttachmentMaxSizeInBytes, forumAttachmentMimeTypes } from '../../forum-attachment-rules';
import { PdfActionIconButtons } from '../../../../components/pdf-actions/pdf-action-icon-buttons';
import { isPdfFile } from '../../../../components/pdf-actions/use-pdf-actions';

const maximumTitleLength = 200;
const maximumContentLength = 4000;

const attachmentLabels = {
  dropZoneHelperText: 'Billeder, PDF og tekstfiler på maks. 10MB',
  selectFilesButton: 'Vedhæft filer',
  invalidFileTypeError: 'Det er ikke tilladt at vedhæfte denne filtype',
  fileTooLargeError: (fileName: string) => `"${fileName}" er større end de tilladte 10MB`,
};

export interface INewsForm {
  title: string;
  content: string;
  url: string;
  showFromDate: DatePickerValue;
  expirationDate: DatePickerValue;
  showToAll: boolean;
  journalize: boolean;
  enableShowInterest: boolean;
  attachments: File[];
  existingAttachments: AttachmentMetaDataModel[];
}

export type NewsFormErrors = Partial<Record<'title' | 'content' | 'showFromDate' | 'expirationDate', string>>;

export function validateNewsForm(form: INewsForm): NewsFormErrors {
  const errors: NewsFormErrors = {};
  const showFromDate = toStartOfDay(form.showFromDate);
  const expirationDate = toStartOfDay(form.expirationDate);

  if (!form.title.trim()) {
    errors.title = 'Titel skal være udfyldt.';
  }
  if (!form.content.trim()) {
    errors.content = 'Besked skal være udfyldt.';
  }
  if (!showFromDate) {
    errors.showFromDate = 'Synlig fra skal være udfyldt.';
  } else if (expirationDate && showFromDate >= expirationDate) {
    errors.showFromDate = 'Synlig fra datoen skal være før udløbsdatoen';
  }
  if (expirationDate && expirationDate <= today()) {
    errors.expirationDate = 'Udløbsdatoen skal ligge i fremtiden';
  }

  return errors;
}

export interface ISavedNewsFlags {
  journalize: boolean;
  enableShowInterest: boolean;
}

export interface ICreateUpdateNewsDialogComponentProps {
  newsItemToUpdate?: NewsItemModel;
  canShowToAll: boolean;
  canAttachFiles: boolean;
  isSaving: boolean;
  savedFlags?: ISavedNewsFlags;
  onSave: (form: INewsForm) => void;
  onCancel: () => void;
  onOpenAttachment: (attachment: AttachmentMetaDataModel) => void;
  attachmentActions?: {
    isFetching: boolean;
    onOpen: (attachment: AttachmentMetaDataModel) => void;
    onDownload: (attachment: AttachmentMetaDataModel) => void;
  };
}

export function CreateUpdateNewsDialogComponent(props: ICreateUpdateNewsDialogComponentProps) {
  const { newsItemToUpdate, canShowToAll, canAttachFiles, isSaving, savedFlags, onSave, onCancel, onOpenAttachment, attachmentActions } = props;
  const isUpdate = !!newsItemToUpdate;

  const [form, setForm] = useState<INewsForm>(() => initialForm(newsItemToUpdate));
  const [errors, setErrors] = useState<NewsFormErrors>({});
  const [attachmentError, setAttachmentError] = useState('');

  function updateForm(changes: Partial<INewsForm>) {
    setForm(current => ({ ...current, ...changes }));
  }

  function removeExistingAttachment(attachmentId: string) {
    updateForm({ existingAttachments: form.existingAttachments.filter(attachment => attachment.id !== attachmentId) });
  }

  function handleShowToAllChange() {
    updateForm({ showToAll: !form.showToAll, journalize: false });
  }

  function handleSave() {
    const foundErrors = validateNewsForm(form);
    setErrors(foundErrors);
    if (Object.keys(foundErrors).length === 0) {
      onSave(form);
    }
  }

  return (
    <FormDialog
      open
      title={isUpdate ? 'Opdater nyhed' : 'Opret nyhed'}
      confirmButtonText={isUpdate ? 'Opdater' : 'Opret'}
      cancelButtonText="Annullér"
      onConfirmClick={handleSave}
      onCancelClick={onCancel}
      onClose={onCancel}
      isLoading={isSaving}
      dontCapitalizeButtonTexts
      disableBackdropClick
      fullWidth
    >
      <Row>
        <Cell size="full">
          <TextField
            label="Titel"
            value={form.title}
            multiline={false}
            required
            autoFocus
            maxLength={maximumTitleLength}
            error={!!errors.title}
            errorText={errors.title}
            helperText={charactersLeft(form.title, maximumTitleLength)}
            onChange={event => updateForm({ title: event.target.value })}
          />
        </Cell>
      </Row>
      <Row>
        <Cell size="full">
          <TextField
            label="Besked"
            value={form.content}
            multiline
            minRows={7}
            maxRows={20}
            required
            maxLength={maximumContentLength}
            error={!!errors.content}
            errorText={errors.content}
            helperText={charactersLeft(form.content, maximumContentLength)}
            onChange={event => updateForm({ content: event.target.value })}
          />
        </Cell>
      </Row>
      {form.existingAttachments.length > 0 && (
        <Row>
          <Cell size="full">
            {form.existingAttachments.map(attachment => (
              <Box key={attachment.id} display="flex" alignItems="center" gap={1}>
                <Link component="button" variant="body2" onClick={() => onOpenAttachment(attachment)}>
                  {attachment.filename}
                </Link>
                {attachmentActions && (
                  <PdfActionIconButtons
                    isPdf={isPdfFile(attachment.mimetype, attachment.filename)}
                    disabled={attachmentActions.isFetching}
                    onOpen={() => attachmentActions.onOpen(attachment)}
                    onDownload={() => attachmentActions.onDownload(attachment)}
                  />
                )}
                <IconButton size="small" aria-label={`Fjern ${attachment.filename}`} title={`Fjern ${attachment.filename}`} onClick={() => removeExistingAttachment(attachment.id)}>
                  <TrashIcon />
                </IconButton>
              </Box>
            ))}
          </Cell>
        </Row>
      )}
      {canAttachFiles && (
        <Row>
          <Cell size="full">
            <MultiFilePicker
              files={form.attachments}
              onFilesChange={attachments => updateForm({ attachments })}
              error={attachmentError}
              onErrorChange={setAttachmentError}
              acceptedMimeTypes={forumAttachmentMimeTypes}
              acceptAttribute={forumAttachmentMimeTypes.join(',')}
              maxFileSizeBytes={forumAttachmentMaxSizeInBytes}
              labels={attachmentLabels}
            />
          </Cell>
        </Row>
      )}
      <Row>
        <Cell size="full">
          <TextField label="Eksternt link" value={form.url} multiline={false} onChange={event => updateForm({ url: event.target.value })} />
        </Cell>
      </Row>
      <Row>
        <Cell size="half">
          <DatePicker
            label="Synlig fra"
            value={form.showFromDate}
            required
            error={!!errors.showFromDate}
            errorText={errors.showFromDate}
            onChange={showFromDate => updateForm({ showFromDate })}
          />
        </Cell>
        <Cell size="half">
          <DatePicker
            label="Synlig til og med"
            value={form.expirationDate}
            error={!!errors.expirationDate}
            errorText={errors.expirationDate}
            onChange={expirationDate => updateForm({ expirationDate })}
          />
        </Cell>
      </Row>
      <Box display="flex" flexWrap="wrap" columnGap={4}>
        {(canShowToAll || isUpdate) && <Checkbox label="Vis til alle" checked={form.showToAll} disabled={isUpdate} onChange={handleShowToAllChange} />}
        <Checkbox
          label="Journalisér nudges"
          checked={isUpdate ? savedFlags?.journalize ?? false : form.journalize}
          disabled={isUpdate || form.showToAll}
          onChange={() => updateForm({ journalize: !form.journalize })}
        />
        <Checkbox
          label="Borgere kan vise interesse"
          checked={isUpdate ? savedFlags?.enableShowInterest ?? false : form.enableShowInterest}
          disabled={isUpdate}
          onChange={() => updateForm({ enableShowInterest: !form.enableShowInterest })}
        />
      </Box>
    </FormDialog>
  );
}

function initialForm(newsItem: NewsItemModel | undefined): INewsForm {
  return {
    title: newsItem?.title ?? '',
    content: newsItem?.content ?? '',
    url: newsItem?.url ?? '',
    showFromDate: newsItem ? toDate(newsItem.showFromDate) : today(),
    expirationDate: toDate(newsItem?.expirationDate),
    showToAll: newsItem?.showToAll ?? false,
    journalize: false,
    enableShowInterest: false,
    attachments: [],
    existingAttachments: newsItem?.attachments ?? [],
  };
}

function charactersLeft(text: string, maximumLength: number) {
  return `${maximumLength - text.length} tegn tilbage`;
}
